import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Fingerprint, LayoutDashboard, ScrollText, Plus, LogOut, Users, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import NotificationStream from "@/components/NotificationStream";
import PushPrompt from "@/components/PushPrompt";
import AssistantDrawer from "@/components/AssistantDrawer";
import { Footer } from "@/components/Footer";
import { unlockAudio } from "@/lib/sound";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Register the first in-app interaction as the gesture that unlocks audio,
  // so notification chimes can play even from an idle background tab.
  useEffect(() => {
    const unlock = () => unlockAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  const nav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/ledger", label: "The Ledger", icon: ScrollText },
    { to: "/directory", label: "Community", icon: Users },
  ];

  return (
    <div className="min-h-screen paper-texture">
      <header className="border-b border-[#DEDBD3] bg-[#F9F8F6]/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <Fingerprint className="text-[#1B4D3E]" size={22} strokeWidth={1.6} />
            <span className="font-serif-dl text-xl font-semibold tracking-tight">Dap Ledger</span>
          </Link>

          <nav className="hidden sm:flex items-center gap-1">
            {nav.map((n) => {
              const active = location.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    active ? "bg-[#F0EBE1] text-[#1B4D3E]" : "text-[#5C5A54] hover:text-[#1A1A1A]"
                  }`}
                >
                  <n.icon size={16} strokeWidth={1.6} />
                  {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="new-dap-btn"
              onClick={() => navigate("/create")}
              className="flex items-center gap-2 bg-[#1B4D3E] text-white rounded-md px-4 py-2 text-sm font-serif-dl tracking-wide hover:bg-[#143A2F] transition-colors"
            >
              <Plus size={16} /> New Dap
            </button>
            <NotificationBell />
            <Link to="/settings" data-testid="settings-link" title="Settings">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-[#DEDBD3]" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#F0EBE1] flex items-center justify-center text-sm font-semibold text-[#1B4D3E]">
                  {user?.name?.[0] || "?"}
                </div>
              )}
            </Link>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="text-[#5C5A54] hover:text-[#9C3A3A] transition-colors"
              title="Log out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <PushPrompt />
      <main className="max-w-6xl mx-auto px-6 py-10 animate-fade-up">{children}</main>
      <Footer />
      <AssistantDrawer />
      <NotificationStream />
    </div>
  );
}
